/**
 * Modal dialog component for displaying overlay content.
 * Uses native HTML dialog element with accessibility features.
 */

import { Component, Input, Output, EventEmitter, HostListener, ViewChild, ElementRef, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';

/**
 * Reusable modal dialog component with accessibility support.
 *
 * @remarks Provides a modal dialog overlay using the native HTML dialog element.
 * Supports keyboard navigation, backdrop clicks, and proper focus management.
 * Automatically handles opening/closing based on visibility state changes.
 *
 * @example
 * ```html
 * <app-dialog
 *   [isVisible]="showDialog"
 *   headline="Confirmation"
 *   text="Are you sure you want to proceed?"
 *   (closed)="onDialogClosed()">
 * </app-dialog>
 * ```
 */
@Component({
  selector: 'app-dialog',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './dialog.component.html',
  styleUrls: ['./dialog.component.scss'],
})
export class DialogComponent implements OnChanges {
  /** @remarks Main headline text displayed in the dialog header */
  @Input() headline = '';
  /** @remarks Body text content of the dialog */
  @Input() text = '';
  /** @remarks Controls the visibility state of the dialog */
  @Input() isVisible = false;
  /** @remarks Event emitted when the dialog is closed by user interaction */
  @Output() closed = new EventEmitter<void>();

  /** @remarks Reference to the native HTML dialog element */
  @ViewChild('dialogRef', { static: true }) dialogRef!: ElementRef<HTMLDialogElement>;

  /**
   * Handles input property changes.
   *
   * @remarks Automatically opens or closes the dialog when the isVisible
   * property changes. Uses the native dialog showModal() and close() methods.
   *
   * @param {SimpleChanges} changes - Object containing the changed properties
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isVisible']) {
      if (this.isVisible) {
        this.openDialog();
      } else {
        this.closeDialog();
      }
    }
  }

  /**
   * Opens the dialog as a modal.
   *
   * @remarks Uses the native HTML dialog showModal() method to display
   * the dialog with proper modal behavior including backdrop and focus trapping.
   */
  private openDialog(): void {
    if (this.dialogRef?.nativeElement && !this.dialogRef.nativeElement.open) {
      this.dialogRef.nativeElement.showModal();
    }
  }

  /**
   * Closes the dialog.
   *
   * @remarks Uses the native HTML dialog close() method to hide the dialog
   * and restore focus to the previously focused element.
   */
  private closeDialog(): void {
    if (this.dialogRef?.nativeElement && this.dialogRef.nativeElement.open) {
      this.dialogRef.nativeElement.close();
    }
  }

  /**
   * Handles dialog close action.
   *
   * @remarks Emits the closed event to notify parent components
   * that the dialog has been dismissed by user interaction.
   */
  onClose(): void {
    this.closed.emit();
  }

  /**
   * Handles Escape key press to close the dialog.
   *
   * @remarks Provides keyboard accessibility by allowing users to close
   * the dialog using the Escape key when it's visible.
   *
   * @param {KeyboardEvent} event - The keyboard event object
   */
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    if (this.isVisible) {
      this.onClose();
    }
  }

  /**
   * Handles backdrop click to close the dialog.
   *
   * @remarks Closes the dialog when users click on the backdrop area
   * (outside the dialog content). Provides intuitive UX for modal dismissal.
   *
   * @param {Event} event - The click event object
   */
  @HostListener('click', ['$event'])
  onDialogClick(event: Event): void {
    if (event.target === this.dialogRef?.nativeElement) {
      this.onClose();
    }
  }
}
